import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import enTranslations from '../i18n/en.json';
import ukTranslations from '../i18n/uk.json';
import type { AppLanguage } from '../types';

type ThemeMode = 'light' | 'dark';
type TranslationMap = Record<string, string>;

const translations: Record<AppLanguage, TranslationMap> = {
  en: enTranslations as TranslationMap,
  uk: ukTranslations as TranslationMap,
};

interface AppSettingsContextValue {
  currentLanguage: AppLanguage;
  switchLanguage: (lang: AppLanguage) => void;
  t: (key: string) => string;
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
}

const AppSettingsContext = createContext<AppSettingsContextValue | null>(null);

const LANGUAGE_STORAGE_KEY = 'app-language';
const THEME_STORAGE_KEY = 'theme';

export function AppSettingsProvider({ children }: { children: React.ReactNode }) {
  const [currentLanguage, setCurrentLanguage] = useState<AppLanguage>(() => {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return saved === 'en' || saved === 'uk' ? saved : 'uk';
  });

  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return saved === 'dark' ? 'dark' : 'light';
  });

  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const switchLanguage = (lang: AppLanguage) => {
    setCurrentLanguage(lang);
  };

  const setTheme = (nextTheme: ThemeMode) => {
    setThemeState(nextTheme);
  };

  const toggleTheme = () => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const t = useMemo(() => {
    return (key: string): string => {
      return (
        translations[currentLanguage][key] ??
        translations.en[key] ??
        key
      );
    };
  }, [currentLanguage]);

  const value: AppSettingsContextValue = {
    currentLanguage,
    switchLanguage,
    t,
    theme,
    setTheme,
    toggleTheme,
  };

  return <AppSettingsContext.Provider value={value}>{children}</AppSettingsContext.Provider>;
}

export function useAppSettings() {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error('useAppSettings must be used within AppSettingsProvider');
  }
  return context;
}
